import axios from "axios";
import { getDefaultStore } from "jotai";
import { chatbotConfigState } from "../../state/config/ChatbotConfigState";
import { TRANSLATIONS } from "../constants/translations";
import { getBackendUrl, extractErrorMessage, buildScopedHeaders } from "./apiHelper";
import { getActiveChatbotId } from "./authUtils";

const BACKEND_URL = getBackendUrl();

function isValidUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || '').trim()
  );
}

function sanitizeChatbotId(value = null) {
  return isValidUuid(value) ? String(value).trim() : null;
}

function getActiveTranslation() {
  try {
    const store = getDefaultStore();
    const cfg = store.get(chatbotConfigState);
    const lang = (cfg?.language || "de").toLowerCase().startsWith("en") ? "en" : "de";
    return TRANSLATIONS[lang] || TRANSLATIONS.de;
  } catch (e) {
    return TRANSLATIONS.de;
  }
}

function resolveChatbotId(explicitChatbotId = null, userAuthToken = null, tenantId = null) {
  return sanitizeChatbotId(
    explicitChatbotId ||
    getActiveChatbotId({
      token: userAuthToken,
      tenantId,
    }) ||
    null
  );
}

export async function getChatbotConfig(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/config`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting chatbot config:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function saveChatbotConfig(userAuthToken, config, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/config`,
      config,
      {
        headers: buildScopedHeaders({
          token: userAuthToken,
          chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
          tenantId,
        }),
      }
    );
    return response.data;
  } catch (error) {
    const t = getActiveTranslation();
    const message = extractErrorMessage(error, t.errorSaved);
    console.error("Error saving chatbot config:", message, error);
    throw new Error(message);
  }
}

export async function getSelectedModelType(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/config/model`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data.modelType;
  } catch (error) {
    console.error("Error getting model type:", extractErrorMessage(error), error);
    return "fast";
  }
}

export async function setModelType(modelType, userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/config/model`,
      { modelType },
      {
        headers: buildScopedHeaders({
          token: userAuthToken,
          chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
          tenantId,
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting model type:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function getCurrentSystemPrompt(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/prompts/current`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data.prompt;
  } catch (error) {
    console.error("Error getting current prompt:", extractErrorMessage(error), error);
    return null;
  }
}

export async function setSystemPrompt(userAuthToken, prompt, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/prompts`,
      { prompt },
      {
        headers: buildScopedHeaders({
          token: userAuthToken,
          chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
          tenantId,
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting system prompt:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function getDefaultSystemPrompt(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/prompts/default`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data.prompt;
  } catch (error) {
    console.error("Error getting default prompt:", extractErrorMessage(error), error);
    return "Sie sind ein hilfreicher Assistent.";
  }
}

export async function getRecentSystemPrompts(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/prompts/recent`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });

    return (response.data || []).map((item) => ({
      content: item.content || item.prompt,
      timestamp: new Date(item.timestamp || item.created_at),
    }));
  } catch (error) {
    console.error("Error getting recent prompts:", extractErrorMessage(error), error);
    return [];
  }
}

export async function uploadLogo(formData, userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.post(`${BACKEND_URL}/storage/logo`, formData, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data.url;
  } catch (error) {
    console.error("Error uploading logo:", extractErrorMessage(error), error);
    throw new Error(extractErrorMessage(error, "Logo upload failed"));
  }
}

export async function deleteMediaInBucket(userAuthToken, domainIdentifiers = null, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/storage/delete-media`,
      {
        ...(Array.isArray(domainIdentifiers) ? { domainIdentifiers } : {}),
        ...(tenantId ? { tenantId } : {}),
      },
      {
        headers: buildScopedHeaders({
          token: userAuthToken,
          chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
          tenantId,
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting media:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function clearCatalog(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    await axios.delete(`${BACKEND_URL}/catalog`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return true;
  } catch (error) {
    console.error("Error clearing catalog:", extractErrorMessage(error), error);
    return false;
  }
}

export async function indexCatalogNodes(nodes, userAuthToken, chatbotId = null, tenantId = null) {
  try {
    await axios.post(
      `${BACKEND_URL}/catalog/index`,
      { nodes, ...(tenantId ? { tenantId } : {}) },
      {
        headers: buildScopedHeaders({
          token: userAuthToken,
          chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
          tenantId,
        }),
      }
    );
    return true;
  } catch (error) {
    console.error("Error indexing catalog nodes:", extractErrorMessage(error), error);
    return false;
  }
}

// ADDED: getCatalogGraph for the Drawio Overview
export async function getCatalogGraph(userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/catalog/graph`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting catalog graph:", extractErrorMessage(error), error);
    return { nodes: [] };
  }
}

export async function getTenantChatbots(userAuthToken, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/admin/chatbots`, {
      headers: buildScopedHeaders({ token: userAuthToken, tenantId }),
    });
    return response.data;
  } catch (error) {
    console.error("Error loading tenant chatbots:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function createChatbot(userAuthToken, payload, tenantId = null) {
  try {
    const response = await axios.post(`${BACKEND_URL}/admin/chatbots`, {
      ...payload,
      ...(tenantId ? { tenantId } : {}),
    }, {
      headers: buildScopedHeaders({ token: userAuthToken, tenantId }),
    });
    return response.data;
  } catch (error) {
    console.error("Error creating chatbot:", extractErrorMessage(error), error);
    throw error;
  }
}

export async function updateChatbot(userAuthToken, chatbotId, payload, tenantId = null) {
  try {
    const response = await axios.put(`${BACKEND_URL}/admin/chatbots/${chatbotId}`, {
      ...payload,
      ...(tenantId ? { tenantId } : {}),
    }, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(chatbotId, userAuthToken, tenantId),
        tenantId,
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating chatbot:", extractErrorMessage(error), error);
    throw error;
  }
}